import { Controller } from 'react-hook-form';
import { differenceInDays } from 'date-fns';
import gettext from '@sdk/lang/gettext';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Picker from '@sdk/components/forms/Picker';
import { FormLabel, FormLayout, FormActions } from '@sdk/components/ui/modalForm/modalForm.styles';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import getNowDate from '@sdk/utils/getNowDate';
import FieldError from '@sdk/components/forms/components/FieldError';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';

import ClipIcon from '@web/assets/svg/Icons/32/attachment-gray.svg';
import DateIcon from '@web/assets/svg/Icons/32/date-gray.svg';
import TitleIcon from '@web/assets/svg/Icons/32/messages-blue.svg';
import DatePickerField from '@web/forms/components/datePicker/DatePickerField/DatePickerField';
import { OutputDateFormatType } from '@web/forms/components/datePicker/datePicker.types';
import { useExportConversationModal } from '@web/conversations/Conversation/ExportConversationModal/exportConversationModal.hooks';
import { ExportMimetype } from '@web/conversations/Conversation/ExportConversationModal/ExportMimetype';

const mimetypeOptions = [
  { value: ExportMimetype.TXT, label: 'Plain Text (.txt)' },
  { value: ExportMimetype.CSV, label: 'Comma Separated Value (.csv)' },
  { value: ExportMimetype.XLSX, label: 'Microsoft Excel (.xlsx)' },
];

type Props = {
  modalState: ModalState;
};

export const ExportConversationModal = ({ modalState }: Props) => {
  const { formMethods, onSubmit, control, startDateValue, endDateValue } =
    useExportConversationModal({ modalState });
  const todayDate = getNowDate();
  const {
    formState: { isSubmitting, isValid, errors },
  } = formMethods;

  const exportConversationContent = (
    <>
      <form>
        <FormLayout>
          <FormLabel>
            {gettext('Export as')} <ClipIcon />
          </FormLabel>

          <div>
            <Controller
              control={control}
              name="mimetype"
              rules={{ required: gettext('Please select a file format') }}
              render={({ field }) => (
                <Picker
                  value={field.value}
                  options={mimetypeOptions}
                  customWidth={430}
                  placeholder={gettext('Select file type')}
                  onChange={field.onChange}
                  invalid={!!errors.mimetype}
                />
              )}
            />

            <FieldError invalid={!!errors.mimetype} error={errors.mimetype?.message} />
          </div>

          <FormLabel>
            {gettext('Start')} <DateIcon />
          </FormLabel>
          <Controller
            control={control}
            name="startDate"
            rules={{ required: gettext('Please add start date') }}
            render={({ field }) => (
              <DatePickerField
                outputFormat={OutputDateFormatType.ddMMMyyyy}
                placeholder={gettext('Add date')}
                date={field.value}
                onDateChange={field.onChange}
                error={errors.startDate?.message}
                invalid={!!errors.startDate}
                maxDate={endDateValue || todayDate}
              />
            )}
          />
          <FormLabel>
            {gettext('End')} <DateIcon />
          </FormLabel>
          <Controller
            control={control}
            name="endDate"
            rules={{
              validate: (endDate) =>
                !startDateValue ||
                differenceInDays(endDate, startDateValue) < 30 ||
                gettext('Please select a date range of 30 days or less'),
              required: gettext('Please add end date'),
            }}
            render={({ field }) => (
              <DatePickerField
                outputFormat={OutputDateFormatType.ddMMMyyyy}
                placeholder={gettext('Add date')}
                date={field.value}
                onDateChange={field.onChange}
                error={errors.endDate?.message}
                invalid={!!errors.endDate}
                maxDate={todayDate}
                minDate={startDateValue}
              />
            )}
          />
        </FormLayout>

        <FormActions>
          <Button onClick={modalState.close}>{gettext('Cancel')}</Button>
          <ProgressButton
            onClick={onSubmit}
            buttonType={isValid ? ButtonType.primary : ButtonType.disabledStyle}
            inactive={!isValid || isSubmitting}>
            {gettext('Export')}
          </ProgressButton>
        </FormActions>
      </form>
    </>
  );

  return (
    <Modal
      fixed
      isVisible={modalState.isVisible}
      onDismiss={modalState.close}
      closePositionTop={10}>
      <ModalBodyWithHeader
        width={620}
        titleThemeColor={ThemeColorType.primary}
        titleText={gettext('Export conversation history')}
        titleIcon={<TitleIcon />}
        close={modalState.close}>
        {exportConversationContent}
      </ModalBodyWithHeader>
    </Modal>
  );
};
